import type { V2AdminCreditDetailDto } from '@idc-forms/api-client'
import { isFieldVisible } from '@idc-forms/formula/show-when'

export type CreditStage = V2AdminCreditDetailDto['stages'][number]
export type CreditItem = CreditStage['items'][number]
export type CreditField = CreditItem['fields'][number]
export type CreditFieldValue = NonNullable<CreditField['value']>
export type CreditFinancingType = V2AdminCreditDetailDto['financingType']

export type CreditStep = {
  stage: CreditStage
  item: CreditItem
}

export type CreditStageIssue = {
  kind: 'item' | 'field'
  severity: 'warning' | 'blocking'
  stageKey: string
  itemKey: string
  itemLabel: string
  fieldKey?: string
  fieldLabel?: string
}

export type CreditStageProgress = {
  checked: number
  total: number
  percentage: number
}

/* ─────────────────────────── «Mostrar sólo si», del lado de la pantalla ─────────────────────── */

/**
 * ⛔ LOS HECHOS DEL EXPEDIENTE. Los manda el SERVIDOR; acá sólo se refrescan mientras se escribe.
 *
 * La pantalla no deriva el mapa: lo recibe en `variableFacts`, armado por el mismo builder con el
 * que el kernel decide si exige un campo. Derivarlo acá sería volver a razonar qué token nombra a
 * qué campo, con qué prefijo y cómo se lee cada valor — cuatro decisiones que el servidor ya tomó, y
 * duplicadas se separan en la primera corrección. El síntoma sería el peor: la pantalla esconde un
 * campo y la etapa no cierra por él.
 */
export type CreditVariableFacts = NonNullable<V2AdminCreditDetailDto['variableFacts']>

/**
 * El token con el que se nombra un campo. Espeja `stageVariablePrefix` del kernel: va por `order`
 * —«lo de la etapa 4»— y no por la `key` de la etapa, que es el JOIN KEY del dato.
 */
export function creditStageVariableToken(stageOrder: number, fieldKey: string): string {
  return `etapa${stageOrder}.${fieldKey}`
}

/**
 * El valor de un campo → el texto con el que compara una condición.
 *
 * ☠️ **Es un ESPEJO de `factOf`** (`workflow-kernel/domain/effect-rules.ts`), y el original manda:
 * las opciones por su CLAVE, el monto en unidades menores, el booleano como `'true'`/`'false'`, y
 * `null` para lo que no se compara (una lista múltiple, un adjunto). No se comparte el código
 * porque los dos lados ven tipos distintos por contrato —el kernel `KernelFieldValue`, el navegador
 * el DTO— y cruzar el paquete por veinte líneas arrastraría la unión entera.
 *
 * Lo que sí acota el riesgo es DÓNDE se usa: sólo para el borrador que se está escribiendo y
 * todavía no se guardó. Todo lo ya guardado llega resuelto por el servidor. Si esto discrepara, la
 * diferencia duraría hasta el siguiente guardado — y las pruebas de abajo la fijan rama por rama.
 */
export function creditFieldFact(value: CreditFieldValue | null): string | null {
  if (!value) return null
  switch (value.kind) {
    case 'text':
      return value.text
    case 'number':
      return String(value.number)
    case 'money':
      return String(value.amountMinor)
    case 'date':
      return value.date
    case 'bool':
      return value.bool ? 'true' : 'false'
    case 'option':
      // Una lista MÚLTIPLE no se compara con una igualdad: no aporta hecho, igual que en el kernel.
      return value.options.length === 1 ? value.options[0] : null
    case 'files':
      return null
  }
}

/**
 * Los hechos del servidor, con lo que se está escribiendo AHORA encima.
 *
 * Es lo que hace que elegir una opción muestre o esconda al campo hermano en el acto, antes de
 * guardar. Sin el solapado habría que guardar y esperar el refetch para ver aparecer el campo que
 * la respuesta acaba de habilitar — y en el ínterin la pantalla estaría mostrando algo que ya no es
 * cierto.
 *
 * ☠️ **Sólo PISA claves que el servidor ya declaró; nunca agrega una.** El dominio de variables lo
 * declara el expediente, no el navegador. Si el servidor no mandó hechos —una respuesta anterior a
 * este cambio— el mapa queda vacío, toda condición cae en el fail-open y **todo se ve**: que es
 * exactamente lo que hace ese backend, que no exime a nadie. Agregando claves, en cambio, la
 * pantalla escondería campos que ese servidor sigue exigiendo.
 */
export function creditFactsWithDrafts(
  facts: CreditVariableFacts | undefined,
  stageOrder: number,
  drafts: Record<string, CreditFieldValue | null>
): CreditVariableFacts {
  const merged: CreditVariableFacts = { ...(facts ?? {}) }
  for (const [fieldKey, value] of Object.entries(drafts ?? {})) {
    const token = creditStageVariableToken(stageOrder, fieldKey)
    if (!Object.hasOwn(merged, token)) continue
    merged[token] = creditFieldFact(value)
  }
  return merged
}

/**
 * ¿Se pinta este campo?
 *
 * Delega en `isFieldVisible`, el MISMO lector que corre el kernel para decidir si lo exige. No es
 * una reimplementación equivalente: es la misma función del mismo paquete, y por eso la pantalla y
 * el backend no pueden discrepar sobre qué campo existe.
 */
export function isCreditFieldVisible(
  field: Pick<CreditField, 'showWhen'>,
  facts: CreditVariableFacts | undefined
): boolean {
  return isFieldVisible(field?.showWhen, facts ?? {})
}

/** Los campos que se pintan, ya ordenados. */
export function visibleCreditFields(
  item: Pick<CreditItem, 'fields'>,
  facts: CreditVariableFacts | undefined
): CreditField[] {
  return item.fields.filter((field) => isCreditFieldVisible(field, facts)).toSorted(byOrder)
}

export type CreditUploadCandidate = { size: number; type: string }

export type CreditUploadValidation =
  | { valid: true }
  | { valid: false; code: 'FILE_TOO_LARGE' | 'FILE_TYPE_NOT_ALLOWED' }

const MAX_CREDIT_UPLOAD_BYTES = 30 * 1024 * 1024

function byOrder<T extends { order: number }>(left: T, right: T) {
  return left.order - right.order
}

export function itemAppliesToFinancing(
  item: Pick<CreditItem, 'appliesTo'>,
  financingType: CreditFinancingType
): boolean {
  return item.appliesTo === 'both' || item.appliesTo === financingType
}

export function visibleCreditItems(
  stage: Pick<CreditStage, 'items'>,
  financingType: CreditFinancingType
): CreditItem[] {
  return stage.items.filter((item) => itemAppliesToFinancing(item, financingType)).toSorted(byOrder)
}

export function buildCreditSteps(
  stages: CreditStage[],
  financingType: CreditFinancingType
): CreditStep[] {
  return stages
    .filter((stage) => stage.status !== 'skipped')
    .toSorted(byOrder)
    .flatMap((stage) => visibleCreditItems(stage, financingType).map((item) => ({ stage, item })))
}

export function findInitialCreditStepIndex(
  steps: CreditStep[],
  currentStageKey: string | null
): number {
  const active = steps.filter((step) => step.stage.key === currentStageKey)
  const firstUnchecked = active.find((step) => !step.item.checked)
  const target = firstUnchecked ?? active.at(-1) ?? steps.at(0)
  return target ? Math.max(0, steps.indexOf(target)) : 0
}

export function findCreditStepIndex(
  steps: CreditStep[],
  stageKey: string | null,
  itemKey: string | null,
  fallback: number
): number {
  const requested = steps.findIndex(
    (step) => step.stage.key === stageKey && step.item.key === itemKey
  )
  return requested >= 0 ? requested : Math.min(Math.max(fallback, 0), Math.max(steps.length - 1, 0))
}

/**
 * ¿A qué paso hay que ir para tapar el hueco de ESTA etapa?
 *
 * Es el destino del panel de `CREDIT_PROCESS_INCOMPLETE`: el 409 dice qué etapas tienen pendientes,
 * pero listarlas sin llevar a ninguna parte deja a la persona buscándolas a mano en el riel.
 *
 * El orden del desempate espeja el del servidor: primero el primer punto SIN MARCAR, y si están
 * todos marcados —el hueco es un campo requerido de un ítem ya sellado— el primer punto al que le
 * falte responder uno. Recién al final, el primero de la etapa: mejor aterrizar en la etapa
 * correcta que no moverse.
 *
 * Devuelve `-1` cuando esa etapa no tiene pasos navegables (quedó fuera del recorrido o ningún
 * ítem aplica a la modalidad); el consumidor no navega.
 */
export function findCreditStageGapIndex(
  steps: CreditStep[],
  stageKey: string,
  facts: CreditVariableFacts | undefined
): number {
  const inStage = steps.filter((step) => step.stage.key === stageKey)
  const unchecked = inStage.find((step) => !step.item.checked)
  const withEmptyRequiredField = inStage.find((step) =>
    visibleCreditFields(step.item, facts).some(
      (field) => field.requirement !== 'optional' && !isCreditFieldAnswered(field)
    )
  )
  const target = unchecked ?? withEmptyRequiredField ?? inStage.at(0)
  return target ? steps.indexOf(target) : -1
}

export function creditStageProgress(
  stage: Pick<CreditStage, 'items'>,
  financingType: CreditFinancingType
): CreditStageProgress {
  const items = visibleCreditItems(stage, financingType)
  const checked = items.filter((item) => item.checked).length
  return {
    checked,
    total: items.length,
    percentage: items.length === 0 ? 0 : Math.round((checked / items.length) * 100)
  }
}

export function isCreditFieldAnswered(field: Pick<CreditField, 'value'>): boolean {
  const value = field.value
  if (!value) return false
  if (value.kind === 'text') return value.text.trim().length > 0
  if (value.kind === 'option') return value.options.length > 0
  if (value.kind === 'files') return value.attachments.length > 0
  return true
}

export function collectCreditStageIssues(
  stage: CreditStage,
  financingType: CreditFinancingType,
  facts: CreditVariableFacts | undefined
): CreditStageIssue[] {
  return visibleCreditItems(stage, financingType).flatMap((item) => {
    const issues: CreditStageIssue[] = item.checked
      ? []
      : [
          {
            kind: 'item',
            severity: 'warning',
            stageKey: stage.key,
            itemKey: item.key,
            itemLabel: item.label
          }
        ]

    // Los ocultos no se cuentan: es la misma exención que hace el kernel, así que la pantalla
    // promete exactamente lo que el avance va a advertir.
    for (const field of visibleCreditFields(item, facts)) {
      if (field.requirement === 'optional' || isCreditFieldAnswered(field)) continue
      issues.push({
        kind: 'field',
        severity: field.requirement === 'blocking' ? 'blocking' : 'warning',
        stageKey: stage.key,
        itemKey: item.key,
        itemLabel: item.label,
        fieldKey: field.key,
        fieldLabel: field.label
      })
    }
    return issues
  })
}

export function creditBlockersFromDetails(details: unknown): CreditStageIssue[] {
  if (!details || typeof details !== 'object') return []
  const blockers = (details as { blockers?: unknown }).blockers
  if (!Array.isArray(blockers)) return []
  return blockers.flatMap((blocker) => {
    if (!blocker || typeof blocker !== 'object') return []
    const value = blocker as Record<string, unknown>
    if (
      typeof value.stageKey !== 'string' ||
      typeof value.itemKey !== 'string' ||
      typeof value.fieldKey !== 'string'
    ) {
      return []
    }
    return [
      {
        kind: 'field' as const,
        severity: 'blocking' as const,
        stageKey: value.stageKey,
        itemKey: value.itemKey,
        itemLabel: typeof value.itemLabel === 'string' ? value.itemLabel : value.itemKey,
        fieldKey: value.fieldKey,
        fieldLabel: typeof value.fieldLabel === 'string' ? value.fieldLabel : value.fieldKey
      }
    ]
  })
}

/**
 * Lo que falta en UNA etapa para poder CERRAR el proceso (`CREDIT_PROCESS_INCOMPLETE`).
 *
 * Es una lista agrupada por etapa, no un listón: la persona lo resuelve etapa por etapa y el
 * mensaje tiene que decirle a dónde ir.
 */
export type CreditProcessGap = {
  stageKey: string
  stageName: string
  /** Etiquetas de lo que falta en esa etapa, ya listas para pintar. */
  missing: string[]
}

/**
 * Traduce el `errors` del 409 de cierre a las líneas que muestra la pantalla.
 *
 * ⚠️ El sobre v2 es RFC 7807: el contenido llega en `errors`, no en `details` — `extractApiError`
 * ya lo normaliza a `details` para la UI. Todo se valida campo por campo porque esto es data del
 * servidor y una lista rota no puede dejar la pantalla sin decir nada.
 */
export function creditProcessGapsFromDetails(details: unknown): CreditProcessGap[] {
  if (!details || typeof details !== 'object') return []
  const stages = (details as { stages?: unknown }).stages
  if (!Array.isArray(stages)) return []

  return stages.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return []
    const stage = entry as Record<string, unknown>
    if (typeof stage.stageKey !== 'string') return []

    const missing = [
      ...toLabels(stage.uncheckedItems, (row) => text(row.itemLabel) ?? text(row.itemKey)),
      ...toLabels(stage.emptyFields, (row) => {
        const item = text(row.itemLabel) ?? text(row.itemKey)
        const field = text(row.fieldLabel) ?? text(row.fieldKey)
        return item && field ? `${item} · ${field}` : null
      })
    ]
    if (missing.length === 0) return []

    return [
      {
        stageKey: stage.stageKey,
        stageName: text(stage.stageName) ?? stage.stageKey,
        missing
      }
    ]
  })
}

function toLabels(
  value: unknown,
  label: (row: Record<string, unknown>) => string | null
): string[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((row) => {
    if (!row || typeof row !== 'object') return []
    const rendered = label(row as Record<string, unknown>)
    return rendered ? [rendered] : []
  })
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

export function countPastUncheckedItems(
  stages: CreditStage[],
  financingType: CreditFinancingType
): number {
  return stages
    .filter((stage) => stage.status === 'completed')
    .flatMap((stage) => visibleCreditItems(stage, financingType))
    .filter((item) => !item.checked).length
}

export function creditRequirementId(stageKey: string, itemKey: string, fieldKey: string): string {
  return `${stageKey}.${itemKey}.${fieldKey}`
}

export function validateCreditUpload(
  file: CreditUploadCandidate,
  config: Extract<CreditField['config'], { kind: 'file' }>
): CreditUploadValidation {
  if (file.size > Math.min(config.maxSizeBytes, MAX_CREDIT_UPLOAD_BYTES)) {
    return { valid: false, code: 'FILE_TOO_LARGE' }
  }
  if (config.allowedMimeTypes.length > 0 && !config.allowedMimeTypes.includes(file.type)) {
    return { valid: false, code: 'FILE_TYPE_NOT_ALLOWED' }
  }
  return { valid: true }
}

export function creditFieldValueEquals(
  left: CreditFieldValue | null,
  right: CreditFieldValue | null
): boolean {
  return JSON.stringify(left) === JSON.stringify(right)
}

export function creditItemQuestion(label: string): string {
  const normalized = label.trim().replace(/[?.!]+$/, '')
  if (!normalized) return '¿Completaste este punto?'
  return `¿Completaste ${normalized.charAt(0).toLowerCase()}${normalized.slice(1)}?`
}

function moneyLabel(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat('es-GT', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2
  }).format(amountMinor / 100)
}

export function formatCreditFieldValue(field: CreditField): string[] {
  const value = field.value
  if (!value) return []

  switch (value.kind) {
    case 'text':
      return value.text.trim() ? [value.text] : []
    case 'number':
      return [new Intl.NumberFormat('es-GT').format(value.number)]
    case 'money':
      return [moneyLabel(value.amountMinor, value.currency)]
    case 'date':
      return [value.date]
    case 'bool':
      return [value.bool ? 'Sí' : 'No']
    case 'files':
      return value.attachments.map((attachment) => attachment.name)
    case 'option': {
      if (field.config.kind !== 'select') return value.options
      const labels = new Map(field.config.options.map((option) => [option.value, option.label]))
      return value.options.map((option) => labels.get(option) ?? option)
    }
  }
}
